import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { AUTH_QUERY_KEYS } from '@/modules/auth/constants';
import { useAuthStore } from '@/modules/auth/hooks';
import type { JourneyStep, PublicUser } from '@/modules/auth/types';
import { COMPANY_QUERY_KEYS } from '@/modules/company/constants';

export const SIGNUP_ROUTES = { WIZARD: '/cadastro', WAITLIST: '/fila-de-espera' } as const;

const ENDPOINT = '/api/v1/signup';

export interface LeadInput {
  name: string;
  whatsapp: string;
  profileType: 'ESTABELECIMENTO' | 'AUTONOMO';
  responsibleName?: string;
  responsibleWhatsapp?: string;
  planId?: string;
  /** So para ESTABELECIMENTO - ja fecham o passo COMPANY_DATA sozinhos. */
  businessType?: string;
  document?: string;
  legalName?: string;
}

export interface VerifyLeadInput {
  verificationId: string;
  whatsapp: string;
  code: string;
}

export interface CompleteLeadInput {
  verificationId: string;
  email: string;
  password: string;
  instagram: string;
  city: string;
  state: string;
}

export interface LeadResponse {
  user: PublicUser;
  token: string | null;
  journeyStep: JourneyStep;
}

export interface WizardState {
  journeyStep: JourneyStep | null;
  /** Fila de espera ligada hoje no SaaS - decide a tela de fechamento do
      wizard, mesmo antes de fechar o ultimo passo. */
  waitlistEnabled: boolean;
}

export interface LeadVerificationResponse {
  verificationId: string;
}

/** Onde a empresa logada esta na jornada - e se a fila de espera esta ligada. */
export function useWizardState() {
  return useQuery({
    queryKey: ['signup', 'journey-state'],
    queryFn: () => apiClient.get<WizardState>(`${ENDPOINT}/wizard/state`),
  });
}

export function useRequestLeadCode() {
  return useMutation({
    mutationFn: (input: LeadInput) =>
      apiClient.post<{ message: string; verificationId: string }>(`${ENDPOINT}/lead/code`, input),
  });
}

/** Confirma o codigo do WhatsApp sem criar a conta antes dos dados finais. */
export function useVerifyLeadCode() {
  return useMutation({
    mutationFn: (input: VerifyLeadInput) =>
      apiClient.post<LeadVerificationResponse>(`${ENDPOINT}/lead/verify`, input),
  });
}

/** Cria a empresa e o usuario depois que o OTP ja foi validado. */
export function useCompleteLead() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (input: CompleteLeadInput) =>
      apiClient.post<LeadResponse>(`${ENDPOINT}/lead/complete`, input),
    onSuccess: async (data) => {
      setUser(data.user);
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
    },
  });
}

/** Fecha o passo atual do wizard e avanca a empresa para o proximo. */
export function useAdvanceWizard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { step: JourneyStep }) =>
      apiClient.post<WizardState>(`${ENDPOINT}/wizard/advance`, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
      await queryClient.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.me });
    },
  });
}
